import * as Schema from "effect/Schema";
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";

import {
  AuthAccessStreamError,
  AuthAccessStreamEvent,
  EnvironmentAuthorizationError,
} from "./auth.ts";
import { FeatureFlagFileError, FeatureFlagPatch, FeatureFlagValueError } from "./featureFlags.ts";
import { KeybindingsConfigError } from "./keybindings.ts";
import {
  ClientOrchestrationCommand,
  ORCHESTRATION_WS_METHODS,
  OrchestrationDispatchCommandError,
  OrchestrationGetSnapshotError,
  OrchestrationReplayEventsError,
  OrchestrationReplayEventsInput,
  OrchestrationRpcSchemas,
} from "./orchestration.ts";
import {
  ProjectCopyOutputError,
  ProjectCopyOutputToAttachmentsInput,
  ProjectCopyOutputToAttachmentsResult,
  ProjectDeleteDocumentError,
  ProjectDeleteDocumentInput,
  ProjectDeleteDocumentResult,
  ProjectDeleteOutputError,
  ProjectDeleteOutputFileInput,
  ProjectDeleteOutputFileResult,
  ProjectDocumentsStatusError,
  ProjectDocumentsStatusInput,
  ProjectDocumentsStatusResult,
  ProjectsUsageReportError,
  ProjectsUsageReportInput,
  ProjectsUsageReportResult,
  ProjectOutputStatusError,
  ProjectOutputStatusInput,
  ProjectOutputStatusResult,
  ProjectSearchEntriesError,
  ProjectSearchEntriesInput,
  ProjectSearchEntriesResult,
} from "./project.ts";
import {
  FeatureFlagAdminState,
  ServerConfigStreamEvent,
  ServerConfig,
  ServerLifecycleStreamEvent,
  ServerRemoveKeybindingInput,
  ServerRemoveKeybindingResult,
  ServerTraceDiagnosticsResult,
  ServerProcessDiagnosticsResult,
  ServerProcessResourceHistoryInput,
  ServerProcessResourceHistoryResult,
  ServerSignalProcessInput,
  ServerSignalProcessResult,
  ServerUpsertKeybindingInput,
  ServerUpsertKeybindingResult,
} from "./server.ts";

export const WS_METHODS = {
  // Project registry methods
  projectsList: "projects.list",
  projectsAdd: "projects.add",
  projectsRemove: "projects.remove",
  projectsSearchEntries: "projects.searchEntries",
  projectsDocumentsStatus: "projects.documentsStatus",
  projectsUsageReport: "projects.usageReport",
  projectsDeleteDocument: "projects.deleteDocument",
  projectsOutputStatus: "projects.outputStatus",
  projectsCopyOutputToAttachments: "projects.copyOutputToAttachments",
  projectsDeleteOutputFile: "projects.deleteOutputFile",

  // Server meta
  serverGetConfig: "server.getConfig",
  serverUpsertKeybinding: "server.upsertKeybinding",
  serverRemoveKeybinding: "server.removeKeybinding",
  serverGetFeatureFlagsAdmin: "server.getFeatureFlagsAdmin",
  serverUpdateFeatureFlags: "server.updateFeatureFlags",
  serverGetTraceDiagnostics: "server.getTraceDiagnostics",
  serverGetProcessDiagnostics: "server.getProcessDiagnostics",
  serverGetProcessResourceHistory: "server.getProcessResourceHistory",
  serverSignalProcess: "server.signalProcess",

  // Streaming subscriptions
  subscribeServerConfig: "subscribeServerConfig",
  subscribeServerLifecycle: "subscribeServerLifecycle",
  subscribeAuthAccess: "subscribeAuthAccess",
} as const;

export const WsServerUpsertKeybindingRpc = Rpc.make(WS_METHODS.serverUpsertKeybinding, {
  payload: ServerUpsertKeybindingInput,
  success: ServerUpsertKeybindingResult,
  error: Schema.Union([KeybindingsConfigError, EnvironmentAuthorizationError]),
});

export const WsServerRemoveKeybindingRpc = Rpc.make(WS_METHODS.serverRemoveKeybinding, {
  payload: ServerRemoveKeybindingInput,
  success: ServerRemoveKeybindingResult,
  error: Schema.Union([KeybindingsConfigError, EnvironmentAuthorizationError]),
});

export const WsServerGetConfigRpc = Rpc.make(WS_METHODS.serverGetConfig, {
  payload: Schema.Struct({}),
  success: ServerConfig,
  error: Schema.Union([KeybindingsConfigError, EnvironmentAuthorizationError]),
});

export const WsServerGetFeatureFlagsAdminRpc = Rpc.make(WS_METHODS.serverGetFeatureFlagsAdmin, {
  payload: Schema.Struct({}),
  success: FeatureFlagAdminState,
  error: EnvironmentAuthorizationError,
});

export const WsServerUpdateFeatureFlagsRpc = Rpc.make(WS_METHODS.serverUpdateFeatureFlags, {
  payload: Schema.Struct({ patch: FeatureFlagPatch }),
  success: FeatureFlagAdminState,
  error: Schema.Union([FeatureFlagFileError, FeatureFlagValueError, EnvironmentAuthorizationError]),
});

export const WsServerGetTraceDiagnosticsRpc = Rpc.make(WS_METHODS.serverGetTraceDiagnostics, {
  payload: Schema.Struct({}),
  success: ServerTraceDiagnosticsResult,
  error: EnvironmentAuthorizationError,
});

export const WsServerGetProcessDiagnosticsRpc = Rpc.make(WS_METHODS.serverGetProcessDiagnostics, {
  payload: Schema.Struct({}),
  success: ServerProcessDiagnosticsResult,
  error: EnvironmentAuthorizationError,
});

export const WsServerGetProcessResourceHistoryRpc = Rpc.make(
  WS_METHODS.serverGetProcessResourceHistory,
  {
    payload: ServerProcessResourceHistoryInput,
    success: ServerProcessResourceHistoryResult,
    error: EnvironmentAuthorizationError,
  },
);

export const WsServerSignalProcessRpc = Rpc.make(WS_METHODS.serverSignalProcess, {
  payload: ServerSignalProcessInput,
  success: ServerSignalProcessResult,
  error: EnvironmentAuthorizationError,
});

export const WsProjectsSearchEntriesRpc = Rpc.make(WS_METHODS.projectsSearchEntries, {
  payload: ProjectSearchEntriesInput,
  success: ProjectSearchEntriesResult,
  error: Schema.Union([ProjectSearchEntriesError, EnvironmentAuthorizationError]),
});

export const WsProjectsDocumentsStatusRpc = Rpc.make(WS_METHODS.projectsDocumentsStatus, {
  payload: ProjectDocumentsStatusInput,
  success: ProjectDocumentsStatusResult,
  error: Schema.Union([ProjectDocumentsStatusError, EnvironmentAuthorizationError]),
});

export const WsProjectsUsageReportRpc = Rpc.make(WS_METHODS.projectsUsageReport, {
  payload: ProjectsUsageReportInput,
  success: ProjectsUsageReportResult,
  error: Schema.Union([ProjectsUsageReportError, EnvironmentAuthorizationError]),
});

export const WsProjectsDeleteDocumentRpc = Rpc.make(WS_METHODS.projectsDeleteDocument, {
  payload: ProjectDeleteDocumentInput,
  success: ProjectDeleteDocumentResult,
  error: Schema.Union([ProjectDeleteDocumentError, EnvironmentAuthorizationError]),
});

export const WsProjectsOutputStatusRpc = Rpc.make(WS_METHODS.projectsOutputStatus, {
  payload: ProjectOutputStatusInput,
  success: ProjectOutputStatusResult,
  error: Schema.Union([ProjectOutputStatusError, EnvironmentAuthorizationError]),
});

export const WsProjectsCopyOutputToAttachmentsRpc = Rpc.make(
  WS_METHODS.projectsCopyOutputToAttachments,
  {
    payload: ProjectCopyOutputToAttachmentsInput,
    success: ProjectCopyOutputToAttachmentsResult,
    error: Schema.Union([ProjectCopyOutputError, EnvironmentAuthorizationError]),
  },
);

export const WsProjectsDeleteOutputFileRpc = Rpc.make(WS_METHODS.projectsDeleteOutputFile, {
  payload: ProjectDeleteOutputFileInput,
  success: ProjectDeleteOutputFileResult,
  error: Schema.Union([ProjectDeleteOutputError, EnvironmentAuthorizationError]),
});

export const WsOrchestrationDispatchCommandRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.dispatchCommand,
  {
    payload: ClientOrchestrationCommand,
    success: OrchestrationRpcSchemas.dispatchCommand.output,
    error: Schema.Union([OrchestrationDispatchCommandError, EnvironmentAuthorizationError]),
  },
);

export const WsOrchestrationReplayEventsRpc = Rpc.make(ORCHESTRATION_WS_METHODS.replayEvents, {
  payload: OrchestrationReplayEventsInput,
  success: OrchestrationRpcSchemas.replayEvents.output,
  error: Schema.Union([OrchestrationReplayEventsError, EnvironmentAuthorizationError]),
});

export const WsOrchestrationGetArchivedShellSnapshotRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.getArchivedShellSnapshot,
  {
    payload: OrchestrationRpcSchemas.getArchivedShellSnapshot.input,
    success: OrchestrationRpcSchemas.getArchivedShellSnapshot.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
  },
);

export const WsOrchestrationSubscribeShellRpc = Rpc.make(ORCHESTRATION_WS_METHODS.subscribeShell, {
  payload: OrchestrationRpcSchemas.subscribeShell.input,
  success: OrchestrationRpcSchemas.subscribeShell.output,
  error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsOrchestrationSubscribeThreadRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.subscribeThread,
  {
    payload: OrchestrationRpcSchemas.subscribeThread.input,
    success: OrchestrationRpcSchemas.subscribeThread.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
    stream: true,
  },
);

export const WsOrchestrationFetchOlderThreadMessagesRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.fetchOlderThreadMessages,
  {
    payload: OrchestrationRpcSchemas.fetchOlderThreadMessages.input,
    success: OrchestrationRpcSchemas.fetchOlderThreadMessages.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
  },
);

export const WsOrchestrationFetchOlderThreadActivitiesRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.fetchOlderThreadActivities,
  {
    payload: OrchestrationRpcSchemas.fetchOlderThreadActivities.input,
    success: OrchestrationRpcSchemas.fetchOlderThreadActivities.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
  },
);

export const WsOrchestrationGetThreadActivityPayloadRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.getThreadActivityPayload,
  {
    payload: OrchestrationRpcSchemas.getThreadActivityPayload.input,
    success: OrchestrationRpcSchemas.getThreadActivityPayload.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
  },
);

export const WsOrchestrationGetTurnInspectionRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.getTurnInspection,
  {
    payload: OrchestrationRpcSchemas.getTurnInspection.input,
    success: OrchestrationRpcSchemas.getTurnInspection.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
  },
);

export const WsSubscribeServerConfigRpc = Rpc.make(WS_METHODS.subscribeServerConfig, {
  payload: Schema.Struct({}),
  success: ServerConfigStreamEvent,
  error: Schema.Union([KeybindingsConfigError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsSubscribeServerLifecycleRpc = Rpc.make(WS_METHODS.subscribeServerLifecycle, {
  payload: Schema.Struct({}),
  success: ServerLifecycleStreamEvent,
  error: EnvironmentAuthorizationError,
  stream: true,
});

export const WsSubscribeAuthAccessRpc = Rpc.make(WS_METHODS.subscribeAuthAccess, {
  payload: Schema.Struct({}),
  success: AuthAccessStreamEvent,
  error: Schema.Union([AuthAccessStreamError, EnvironmentAuthorizationError]),
  stream: true,
});

/** Query parameter a client adds to the websocket URL with its bundle version. */
export const WS_CLIENT_VERSION_QUERY_PARAM = "v";
/** Close code the server answers with when the connecting client's version differs. */
export const STALE_CLIENT_CLOSE_CODE = 4409;
/** Close reason prefix; the server version follows it. */
export const STALE_CLIENT_CLOSE_REASON_PREFIX = "stale-client ";

export const WsRpcGroup = RpcGroup.make(
  WsServerGetConfigRpc,
  WsServerUpsertKeybindingRpc,
  WsServerRemoveKeybindingRpc,
  WsServerGetFeatureFlagsAdminRpc,
  WsServerUpdateFeatureFlagsRpc,
  WsServerGetTraceDiagnosticsRpc,
  WsServerGetProcessDiagnosticsRpc,
  WsServerGetProcessResourceHistoryRpc,
  WsServerSignalProcessRpc,
  WsProjectsSearchEntriesRpc,
  WsProjectsDocumentsStatusRpc,
  WsProjectsUsageReportRpc,
  WsProjectsDeleteDocumentRpc,
  WsProjectsOutputStatusRpc,
  WsProjectsCopyOutputToAttachmentsRpc,
  WsProjectsDeleteOutputFileRpc,
  WsSubscribeServerConfigRpc,
  WsSubscribeServerLifecycleRpc,
  WsSubscribeAuthAccessRpc,
  WsOrchestrationDispatchCommandRpc,
  WsOrchestrationReplayEventsRpc,
  WsOrchestrationGetArchivedShellSnapshotRpc,
  WsOrchestrationSubscribeShellRpc,
  WsOrchestrationSubscribeThreadRpc,
  WsOrchestrationFetchOlderThreadMessagesRpc,
  WsOrchestrationFetchOlderThreadActivitiesRpc,
  WsOrchestrationGetThreadActivityPayloadRpc,
  WsOrchestrationGetTurnInspectionRpc,
);
